import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, ExternalLink, MessageCircle, Send, Save, AlertTriangle,
  ShieldCheck, History, User, GraduationCap, Bell, Lock, RefreshCw, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api, fileDownloadUrl, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { FollowupTab } from "../components/FollowupTab";
import { SaveToCustomerBtn } from "../components/SaveToCustomerBtn";

const STATUS_BADGE = {
  "Received": "bg-slate-100 text-slate-800",
  "Documents Checking": "bg-yellow-100 text-yellow-900",
  "In Process": "bg-blue-100 text-blue-800",
  "Completed": "bg-green-100 text-green-800",
  "Rejected": "bg-red-100 text-red-800",
  "Transferred": "bg-purple-100 text-purple-800",
  "Assigned": "bg-indigo-100 text-indigo-800",
};

const ADMIN_STATUSES = ["Received", "Documents Checking", "In Process", "Completed", "Rejected"];

const RequestWorkspacePage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [tab, setTab] = useState("details");

  useEffect(() => {
    const tk = loadAuthToken();
    if (!tk) { navigate("/admin/login"); return; }
    setToken(tk);
    try { setMe(JSON.parse(localStorage.getItem("ap_admin_user") || "null")); } catch {}
    load();
    const iv = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [leadId]);

  const load = async () => {
    try {
      const r = await api.get(`/workspace/leads/${leadId}`);
      setLead(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else if (e?.response?.status === 403) { toast.error("Not allowed"); navigate("/admin"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  if (loading || !lead) return <div className="max-w-5xl mx-auto p-8 text-center text-slate-500">Loading workspace…</div>;

  const isCentral = me?.role === "super_admin" || me?.role === "central_operator";
  const otpVerified = lead.otp?.verified;
  const needInfoActive = lead.need_info?.active;

  return (
    <div data-testid="workspace-page" className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="workspace-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="rounded-xl bg-white border border-slate-200 p-6 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">Tracking ID</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-slate-900">{lead.request_id}</div>
            <div className="mt-2 text-sm text-slate-600">
              <span className="font-medium">{lead.name}</span> · {lead.mobile} · {lead.service_name}
              {lead.custom_work && <> · {lead.custom_work}</>}
            </div>
            <div className="mt-1 flex flex-wrap gap-2 items-center text-xs">
              <Badge className={STATUS_BADGE[lead.status]}>{lead.status}</Badge>
              <Badge className={lead.source === "branch" ? "bg-purple-100 text-purple-800" : "bg-teal-100 text-teal-800"}>{lead.source === "branch" ? `Branch · ${lead.source_branch_name || ""}` : "Website"}</Badge>
              {lead.claimed_by_name && <Badge className="bg-indigo-100 text-indigo-800"><User className="w-3 h-3 mr-1" />{lead.claimed_by_name}</Badge>}
              {otpVerified && <Badge className="bg-green-100 text-green-800"><ShieldCheck className="w-3 h-3 mr-1" /> OTP Verified</Badge>}
              {needInfoActive && <Badge className="bg-red-100 text-red-800 animate-pulse"><Bell className="w-3 h-3 mr-1" /> Info Needed</Badge>}
            </div>
          </div>
          <a
            href={`https://wa.me/${(lead.mobile || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Namaste ${lead.name}, aapka Ashish Point request ${lead.request_id} — status: ${lead.status}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Customer
          </a>
          {isCentral && <SaveToCustomerBtn lead={lead} onDone={load} />}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full overflow-x-auto justify-start sm:grid sm:grid-cols-8">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="need-info" data-testid="tab-need-info">Need Info{needInfoActive && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">Live Chat</TabsTrigger>
          <TabsTrigger value="otp" data-testid="tab-otp">OTP</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="result" data-testid="tab-result">Result</TabsTrigger>
          <TabsTrigger value="followup" data-testid="tab-followup">Follow-up</TabsTrigger>
        </TabsList>

        <TabsContent value="details"><DetailsTab lead={lead} isCentral={isCentral} onUpdate={load} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab lead={lead} token={token} /></TabsContent>
        <TabsContent value="need-info"><NeedInfoTab lead={lead} isCentral={isCentral} onUpdate={load} /></TabsContent>
        <TabsContent value="chat"><ChatTab leadId={leadId} me={me} /></TabsContent>
        <TabsContent value="otp"><OtpTab lead={lead} isCentral={isCentral} onUpdate={load} /></TabsContent>
        <TabsContent value="history"><HistoryTab lead={lead} /></TabsContent>
        <TabsContent value="result"><ResultTab lead={lead} token={token} isCentral={isCentral} onUpdate={load} /></TabsContent>
        <TabsContent value="followup"><FollowupTab lead={lead} /></TabsContent>
      </Tabs>
    </div>
  );
};

const DetailsTab = ({ lead, isCentral, onUpdate }) => {
  const [status, setStatus] = useState(lead.status);
  const [note, setNote] = useState(lead.admin_note || "");
  const [reason, setReason] = useState(lead.reject_reason || "");
  const [saving, setSaving] = useState(false);

  const saveAndWhatsApp = async (openWa) => {
    setSaving(true);
    try {
      const body = { status, admin_note: note };
      if (status === "Rejected") body.reject_reason = reason;
      await api.patch(`/admin/leads/${lead.id}`, body);
      toast.success("Status saved");
      if (openWa && status !== lead.status) {
        try {
          const r = await api.get("/admin/wa-template", { params: { status, lead_id: lead.id } });
          window.open(r.data.wa_url, "_blank", "noopener,noreferrer");
        } catch (e) { toast.error("WhatsApp URL banane me error"); }
      }
      onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const statusChanged = status !== lead.status;

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3 space-y-4 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Info label="Address" value={lead.address} full />
        <Info label="Email" value={lead.email || "—"} />
        <Info label="Created" value={new Date(lead.created_at).toLocaleString()} />
        <Info label="Updated" value={new Date(lead.updated_at).toLocaleString()} />
        {lead.deadline && <Info label="Deadline" value={lead.deadline} />}
        {lead.notes && <Info label="Customer Notes" value={lead.notes} full />}
      </div>

      {lead.dynamic_fields && Object.keys(lead.dynamic_fields).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Service-specific Fields</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 rounded-md p-3">
            {Object.entries(lead.dynamic_fields).map(([k, v]) => (
              <div key={k} className="text-xs"><span className="font-medium text-slate-600">{k}:</span> <span className="text-slate-900">{String(v)}</span></div>
            ))}
          </div>
        </div>
      )}

      {isCentral && (
        <div className="border-t pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Public Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="ws-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ADMIN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Note for customer (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ws-note" /></div>
            {status === "Rejected" && (
              <div className="col-span-2"><Label className="text-xs">Reason</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ws-reason" /></div>
            )}
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button onClick={() => saveAndWhatsApp(true)} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="ws-save-wa-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Status Save + WhatsApp Open"}
            </Button>
            <Button variant="outline" onClick={() => saveAndWhatsApp(false)} disabled={saving} data-testid="ws-save-btn">
              <Save className="w-4 h-4 mr-2" /> Save only
            </Button>
            {!statusChanged && <span className="text-xs text-slate-500 self-center">(WhatsApp open hoga sirf jab status actually change ho)</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const DocumentsTab = ({ lead, token }) => (
  <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3 space-y-3">
    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Uploaded Documents</div>
    {(lead.files || []).length === 0 ? <div className="text-slate-500 text-sm">None</div> :
      <ul className="space-y-2">
        {(lead.files || []).map((f) => (
          <li key={f.file_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-teal-700" /><div className="text-sm truncate">{f.original_filename}</div></div>
            <a href={fileDownloadUrl(f.file_id, token)} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline text-xs" data-testid={`ws-file-${f.file_id}`}>Open <ExternalLink className="w-3 h-3 inline" /></a>
          </li>
        ))}
      </ul>}
  </div>
);

const NeedInfoTab = ({ lead, isCentral, onUpdate }) => {
  const [message, setMessage] = useState("");
  const [fields, setFields] = useState("");
  const [saving, setSaving] = useState(false);

  const raise = async () => {
    if (!message.trim()) { toast.error("Message required"); return; }
    setSaving(true);
    try {
      const fieldList = fields.split("\n").map((l) => l.trim()).filter(Boolean).map((label) => ({ key: label.toLowerCase().replace(/[^a-z0-9]/g, "_"), label, type: "text" }));
      await api.post(`/workspace/leads/${lead.id}/need-info`, { message: message.trim(), fields: fieldList });
      toast.success("Info request sent to branch/customer");
      setMessage(""); setFields(""); onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resolve = async () => {
    try { await api.post(`/workspace/leads/${lead.id}/need-info/resolve`); toast.success("Marked resolved"); onUpdate(); }
    catch (e) { toast.error("Failed"); }
  };

  const ni = lead.need_info;
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3 space-y-4">
      {ni && ni.active && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Pending Info Request</div>
              <div className="text-sm text-slate-800 mt-1">{ni.message}</div>
              {ni.fields?.length > 0 && (
                <ul className="text-xs text-slate-700 list-disc ml-4 mt-2">
                  {ni.fields.map((f, i) => <li key={i}>{f.label}</li>)}
                </ul>
              )}
              <div className="text-xs text-slate-500 mt-2">Raised by {ni.raised_by_name} · {new Date(ni.raised_at).toLocaleString()}</div>
            </div>
            <Button variant="outline" size="sm" onClick={resolve} data-testid="resolve-need-info">Mark Resolved</Button>
          </div>
        </div>
      )}
      {ni && !ni.active && ni.resolved_at && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4">
          <div className="text-xs uppercase tracking-widest text-green-700 font-semibold">Resolved</div>
          <div className="text-sm mt-1">Customer/Branch has responded. Answers: {JSON.stringify(ni.extra_answers || {})}</div>
          {ni.extra_files?.length > 0 && <div className="text-xs mt-1">{ni.extra_files.length} file(s) uploaded.</div>}
        </div>
      )}
      {isCentral && (
        <div className="border-t pt-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Raise a new Info Request</div>
          <Label className="text-xs">Message to Branch/Customer *</Label>
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Please share old passport scan…" data-testid="ni-message" />
          <Label className="text-xs mt-3 block">Missing fields (one per line, optional)</Label>
          <Textarea rows={3} value={fields} onChange={(e) => setFields(e.target.value)} placeholder="Old Passport Number&#10;Date of Issue" data-testid="ni-fields" />
          <Button onClick={raise} disabled={saving} className="mt-3 bg-red-600 hover:bg-red-700 text-white" data-testid="ni-raise-btn"><Bell className="w-4 h-4 mr-2" /> {saving ? "Sending…" : "Send Info Request"}</Button>
        </div>
      )}
    </div>
  );
};

const ChatTab = ({ leadId, me }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    try { const r = await api.get(`/workspace/leads/${leadId}/chat`); setMsgs(r.data || []); }
    catch (e) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [leadId]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/workspace/leads/${leadId}/chat`, { text: text.trim() }); setText(""); load(); }
    catch (e) { toast.error("Failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-lg bg-white border border-slate-200 mt-3 flex flex-col h-[60vh]">
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.length === 0 && <div className="text-center text-slate-400 text-sm py-10">No messages yet. Start the conversation.</div>}
        {msgs.map((m) => {
          const mine = m.sender_name === (me?.name || me?.email);
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-teal-600 text-white" : m.sender_role === "customer" ? "bg-orange-100 text-slate-900" : "bg-slate-100 text-slate-900"}`}>
                <div className="text-xs opacity-80 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                <div className="text-[10px] opacity-60 mt-1">{new Date(m.ts).toLocaleTimeString()}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t p-3 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" data-testid="chat-input" />
        <Button onClick={send} disabled={sending} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="chat-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

const OtpTab = ({ lead, isCentral, onUpdate }) => {
  const [otpInfo, setOtpInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadOtp = async () => {
    try { const r = await api.get(`/workspace/leads/${lead.id}/otp/status`); setOtpInfo(r.data); } catch {}
  };
  useEffect(() => { loadOtp(); /* eslint-disable-next-line */ }, [lead.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/leads/${lead.id}/otp/generate`);
      toast.success(`OTP: ${r.data.code} (shown once, 10 min)`);
      loadOtp(); onUpdate();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-5 h-5 text-teal-700" />
        <div className="text-lg font-semibold">Customer Consent OTP</div>
      </div>
      <p className="text-sm text-slate-600 mb-4">Generate a 6-digit OTP that the customer confirms on the Track page — proves the customer is present at the branch and consents to the operation.</p>

      {otpInfo?.has_otp && (
        <div className={`rounded-md p-4 mb-3 ${otpInfo.verified ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
          <div className="font-medium">{otpInfo.verified ? "✓ Verified" : "⏳ Awaiting verification"}</div>
          <div className="text-xs text-slate-600 mt-1">
            {otpInfo.verified ? `Verified at ${new Date(otpInfo.verified_at).toLocaleString()}` : `Expires at ${new Date(otpInfo.expires_at).toLocaleString()} · attempts: ${otpInfo.attempts}`}
          </div>
        </div>
      )}

      {isCentral && !otpInfo?.verified && (
        <Button onClick={generate} disabled={busy} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="otp-gen-btn">{busy ? "Generating…" : "Generate OTP"}</Button>
      )}
    </div>
  );
};

const HistoryTab = ({ lead }) => (
  <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3">
    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1"><History className="w-4 h-4" /> Activity Log</div>
    {(lead.history || []).length === 0 ? <div className="text-slate-500 text-sm">No events yet.</div> :
      <ul className="space-y-2">
        {[...(lead.history || [])].reverse().map((h) => (
          <li key={h.id} className="border-l-2 border-teal-500 pl-3 py-1">
            <div className="text-sm"><span className="font-medium">{h.action.replace(/_/g, " ")}</span> · <span className="text-slate-600">{h.by_name}</span> <span className="text-xs text-slate-400">({h.by_role})</span></div>
            <div className="text-xs text-slate-500">{new Date(h.ts).toLocaleString()}</div>
            {h.meta && Object.keys(h.meta).length > 0 && <div className="text-xs text-slate-500 mt-1">{JSON.stringify(h.meta)}</div>}
          </li>
        ))}
      </ul>
    }
  </div>
);

const ResultTab = ({ lead, token, isCentral, onUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [studentData, setStudentData] = useState({ program: "", university: "", roll_no: "", session: "" });
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const receiptRef = useRef(null);
  const isEdu = ["admission", "scholarship"].includes(lead.service_key) || (lead.service_name || "").toLowerCase().includes("ignou");

  const saveStudent = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/workspace/leads/${lead.id}/save-as-student`, studentData);
      toast.success(`Saved as student · ID: ${r.data.id.slice(0, 8)}`);
      onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const uploadReceipts = async () => {
    if (receiptFiles.length === 0) { toast.error("Pick file(s) first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      receiptFiles.forEach((f) => fd.append("files", f));
      await api.post(`/workspace/leads/${lead.id}/central-receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded");
      setReceiptFiles([]);
      onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6 mt-3 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Receipts / Results</div>
        {(lead.receipt_files || []).length === 0 ? <div className="text-slate-500 text-sm">No receipts uploaded yet.</div> :
          <ul className="space-y-2">
            {lead.receipt_files.map((f) => (
              <li key={f.file_id} className="flex items-center justify-between rounded-md border border-green-200 bg-green-50/40 px-3 py-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-700" />
                  <span className="text-sm">{f.original_filename}</span>
                  <Badge className={f.source === "central" ? "bg-teal-100 text-teal-800 text-xs" : "bg-purple-100 text-purple-800 text-xs"}>
                    {f.source === "central" ? "Central" : "Partner"}
                  </Badge>
                </div>
                <a href={fileDownloadUrl(f.file_id, token)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline text-xs">Open <ExternalLink className="w-3 h-3 inline" /></a>
              </li>
            ))}
          </ul>
        }
      </div>

      {isCentral && (
        <div className="border-t pt-4">
          <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">Upload Completion Receipt</div>
          <p className="text-xs text-slate-500 mb-2">Central team here uploads the final receipt / proof after completing the work. Customer will see this on the Track page.</p>
          <Button variant="outline" size="sm" onClick={() => receiptRef.current?.click()} data-testid="central-receipt-pick">
            <Upload className="w-3 h-3 mr-1" /> Choose files
          </Button>
          <input ref={receiptRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => setReceiptFiles([...receiptFiles, ...Array.from(e.target.files || [])])} />
          {receiptFiles.length > 0 && (
            <>
              <ul className="mt-2 space-y-1 text-xs">
                {receiptFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-1 text-slate-700">
                    <FileText className="w-3 h-3" /> {f.original_filename || f.name}
                    <button type="button" onClick={() => setReceiptFiles(receiptFiles.filter((_, x) => x !== i))} className="ml-auto text-red-600">✕</button>
                  </li>
                ))}
              </ul>
              <Button onClick={uploadReceipts} disabled={uploading} size="sm" className="mt-2 bg-teal-700 hover:bg-teal-800 text-white" data-testid="central-receipt-upload"><Upload className="w-3 h-3 mr-1" /> {uploading ? "Uploading…" : "Upload Receipt"}</Button>
            </>
          )}
        </div>
      )}

      {isEdu && isCentral && (
        <div className="border-t pt-4">
          <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2 flex items-center gap-1"><GraduationCap className="w-4 h-4" /> Save as Student</div>
          {lead.student_id ? (
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-900">Already saved as student · ID: {lead.student_id}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Program (BAG / MBA)" value={studentData.program} onChange={(e) => setStudentData({ ...studentData, program: e.target.value })} data-testid="save-student-program" />
                <Input placeholder="University" value={studentData.university} onChange={(e) => setStudentData({ ...studentData, university: e.target.value })} data-testid="save-student-university" />
                <Input placeholder="Roll / Enrol No." value={studentData.roll_no} onChange={(e) => setStudentData({ ...studentData, roll_no: e.target.value })} />
                <Input placeholder="Session (e.g. Jan 2026)" value={studentData.session} onChange={(e) => setStudentData({ ...studentData, session: e.target.value })} />
              </div>
              <Button onClick={saveStudent} disabled={saving} className="mt-3 bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-as-student-btn">
                <GraduationCap className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save as Student"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
    <div className="mt-0.5 text-slate-800 whitespace-pre-wrap">{value}</div>
  </div>
);

export default RequestWorkspacePage;
